import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { useAppContext } from '../../appContext';
import { useStateContext } from '../../store/stateContext';
import CommentCard from '../comment';
import NewComment from '../comments/new-comment';
import Footer from '../footer';

export const COOKIE_NAME = 'user-pop-proactive-cookie';
interface IPrimaryCommentProps { }

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const Container = styled.div<{ isDarkMode: boolean; widgetAlignment: string; showPadding?: boolean }>`
    width: 360px !important;
    max-width: 360px !important;
    box-sizing: border-box !important;
    background-color: ${props => props.isDarkMode ? 'white' : '#585568'} !important;
    #${props => props.showPadding && css`padding: 16px !important; /* 1rem = 16px */`}
    ${props => props.showPadding && css`padding: 16px 16px 10px !important; /* 1rem = 16px */`}
    overflow: hidden !important;
    
    ${props => {
        if (props.widgetAlignment === 'leftbottom') {
            return css`border-radius: 16px !important;`;
        } else if (props.widgetAlignment === 'rightbottom') {
            return css`border-radius: 16px !important;`;
        } else if (props.widgetAlignment === 'leftcenter') {
            return css`
            border-radius: 16px !important;
            border-bottom-left-radius: 0 !important;
            border-top-left-radius: 0 !important;
            `;
        } else if (props.widgetAlignment === 'rightcenter') {
            return css`
                border-radius: 16px !important;
                border-bottom-right-radius: 0 !important;
                border-top-right-radius: 0 !important;
            `;
        }
        return css`border-radius: 16px !important; `;
    }}
    
    @media (max-width: 768px) {
        width: 100% !important;
        max-width: 100% !important;
        
        ${props => {
        if (props.widgetAlignment === 'leftbottom' || props.widgetAlignment === 'rightbottom') {
            return css`border-radius: 16px !important; margin-bottom: 8px !important`;
        } else if (props.widgetAlignment === 'leftcenter') {
            return css`border-radius: 0 16px 16px 0 !important;`;
        } else if (props.widgetAlignment === 'rightcenter') {
            return css`border-radius: 16px 0 0 16px !important;`;
        }
        return css`border-radius: 16px 16px 0 0 !important;`;
    }}
    }
`;

const StyledCommentCard = styled(CommentCard) <{ isVisible: boolean }>`
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  transition: opacity 0.5s ease-in-out !important;
  opacity: ${props => props.isVisible ? 1 : 0} !important;
  animation: ${props => props.isVisible ?
        css`${fadeIn} 0.3s ease-in-out !important` :
        css`${fadeOut} 0.3s ease-in-out !important`};
  border-radius: 16px !important; /* 1rem = 16px */
  overflow: hidden !important;
        
  @media (max-width: 768px) {
    width: 100% !important;
    min-width: unset !important;
    max-width: 100% !important;
  }
`;

const FooterWrapper = styled.div`
    margin-top: 10px !important; /* 0.4rem = 6.4px */
`;


const PrimaryComment: React.FunctionComponent<IPrimaryCommentProps> = () => {
    const { state } = useStateContext();
    const isDarkMode = state.themeMode === 'dark';
    const { widgetAlignment, clearTimers } = useAppContext();
    const [currentCommentIndex, setCurrentCommentIndex] = React.useState(0);
    const [isVisible, setIsVisible] = React.useState(true);

    // Use refs to track timer and timeout
    const timerIdRef = React.useRef<any>(null);
    const timeoutIdRef = React.useRef<any>(null);

    // Find all approved primary comments
    const approvedComments = state.data.data.filter(comment => comment.approved === true);
    const sortedPrimaryComments = approvedComments.filter(comment => comment.primary === true);
    // Removed sorting because backend is already sending sorted by likes and the first one being polls
    // const sortedPrimaryComments = primaryComments.sort((a, b) => b.like_count - a.like_count);

    // Cancel both timers (interval and timeout)
    const handleCancel = () => {
        if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
        }
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    };

    React.useEffect(() => {
        // Set cookie to expire in 1 day
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 1);
        document.cookie = `${COOKIE_NAME}=true; expires=${expirationDate.toUTCString()}; path=/`;
    }, []);

    // Timer setup
    React.useEffect(() => {
        // Start interval for proactive comment switching
        timerIdRef.current = setInterval(() => {
            setIsVisible(false);

            // Timeout for fade animation
            timeoutIdRef.current = setTimeout(() => {
                setCurrentCommentIndex((prevIndex) => {
                    const nextIndex = (prevIndex + 1);
                    if (nextIndex === sortedPrimaryComments.length || sortedPrimaryComments.length === 0) {
                        clearTimers(); // Reset interaction if no more comments
                        return prevIndex;
                    }
                    return nextIndex;
                });
                setIsVisible(true);
            }, 2000); // Duration of the fade-out animation

        }, (state.productInfo?.proactive_timer ?? 5) * 1000); // Use proactive_timer or default to 5 seconds

        // Cleanup both the interval and timeout when the component unmounts
        return () => {
            handleCancel();
        };
    }, [sortedPrimaryComments.length, state.productInfo?.proactive_timer, clearTimers]);

    // Handle no comments
    if (sortedPrimaryComments.length === 0) {
        if (approvedComments.length === 0) {
            return (
                <Container isDarkMode={isDarkMode} widgetAlignment={widgetAlignment} showPadding>
                    <NewComment
                        title={state.productInfo?.default_proactive_title ?? 'Leave us your feedback!'}
                        message={state.productInfo?.default_proactive_message ?? 'Please share your thoughts about this product'}
                        onFocus={() => {
                            handleCancel(); // Cancel the timers on focus
                        }}
                        onCloseCallback={clearTimers}
                    />
                    <FooterWrapper>
                        <Footer isDarkMode={isDarkMode} />
                    </ FooterWrapper>
                </Container>
            );
        } else {
            return (
                <Container isDarkMode={isDarkMode} widgetAlignment={widgetAlignment}>
                    <StyledCommentCard
                        comment={approvedComments[0]}
                        background={isDarkMode ? 'white' : '#585568'}
                        isVisible={isVisible}
                        fullWidth={true}
                        shadow={false}
                        isPrimaryComment={true}
                    />
                </Container>
            );
        }
    }

    return (
        <Container isDarkMode={isDarkMode} widgetAlignment={widgetAlignment}>
            <StyledCommentCard
                comment={sortedPrimaryComments[currentCommentIndex]}
                background={isDarkMode ? 'white' : '#585568'}
                isVisible={isVisible}
                fullWidth={true}
                shadow={false}
                isPrimaryComment={true}
            />
        </Container>
    );
};

export default PrimaryComment;
